import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';

import { OrgUnitSelectionStatus } from 'src/constants/orgUnit';
import useMultiSelect from 'src/hooks/useMultiSelect';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import {
  getInitialOrgUnits,
  orgUnitToFilter,
} from 'src/components/common/OrgTree/helper';
import useOrgTree from 'src/components/common/OrgTree/OrgTreeContext';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import useInitialFilterGroups from 'src/components/followUp/hooks/useInitialFilterGroups';

/**
 * This hook is for controlling the select state of the multi-select org tree. It mainly serves two purposes:
 * - Updating the org tree with an initial selected org unit
 * - Providing the select-function that will be called on interaction with the org-tree
 */
const useSelectOrgUnit = (
  selected: string[],
  setSelected: Dispatch<SetStateAction<string[]>>,
  orgTreeState: OrgTreeState,
  syncWithFilter = true,
) => {
  const { initialFilterGroups } = useInitialFilterGroups();
  const { filterGroups, registerOnResetCallback, setFilter } =
    useFilterContext();
  const { orgUnitMap, disableOrgUnitSelectionBasedOnStatus } = useOrgTree();
  const ctrlDown = useMultiSelect();

  const setInitialOrgUnits = useCallback(() => {
    const initialOrgUnits = getInitialOrgUnits(initialFilterGroups, orgUnitMap);

    if (initialOrgUnits.length && syncWithFilter) {
      setSelected(initialOrgUnits.map(({ id }) => String(id)));
      orgTreeState.setSelectedOrgUnits(initialOrgUnits);
    }
  }, [
    initialFilterGroups,
    orgUnitMap,
    orgTreeState,
    setSelected,
    syncWithFilter,
  ]);

  // Set initial org unit from filters if a filter with an org unit exists
  useEffect(() => {
    if (selected.length || !filterGroups.orgUnit) return;

    setInitialOrgUnits();
  }, [filterGroups.orgUnit, selected, setInitialOrgUnits]);

  useEffect(() => {
    const unregister = registerOnResetCallback(setInitialOrgUnits);

    return () => {
      unregister();
    };
  }, [registerOnResetCallback, setInitialOrgUnits]);

  return useCallback(
    (orgUnitId: string, isSelected: boolean) => {
      if (!(orgUnitId in orgUnitMap)) return;

      const { orgUnit } = orgUnitMap[orgUnitId] || {};
      let selectedOrgUnits = [...orgTreeState.selectedOrgUnits, orgUnit];
      // If isSelected is false it means that an org unit is about to be deselected
      if (!isSelected) {
        // Early return if only one selected org unit is left because at least one org unit needs to be selected
        if (orgTreeState.selectedOrgUnits.length < 2) return;

        setSelected((prev) =>
          prev.length > 1 ? prev.filter((id) => id !== orgUnitId) : prev,
        );
        selectedOrgUnits = orgTreeState.selectedOrgUnits.filter(
          ({ id }) => id !== orgUnit.id,
        );
        orgTreeState.setLatestSelectedOrgUnit(undefined);
      } else {
        // Early return if selected org unit is already selected because we do not want to select it twice
        if (orgTreeState.selectedOrgUnits.includes(orgUnit)) return;

        // Early return if org unit status should prevent it from being selected
        if (
          disableOrgUnitSelectionBasedOnStatus &&
          'selection_status' in orgUnit &&
          (orgUnit.selection_status ===
            OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT ||
            orgUnit.selection_status ===
              OrgUnitSelectionStatus.NEGATIVE_SELECTION)
        )
          return;

        // Depending on if it is a multi-select operation or not the selected org unit needs to be added or replaced
        if (ctrlDown) {
          setSelected((prev) => [...prev, orgUnitId]);
        } else {
          setSelected([orgUnitId]);
          selectedOrgUnits = [orgUnit];
        }
        orgTreeState.setLatestSelectedOrgUnit(orgUnit);
      }

      orgTreeState.setSelectedOrgUnits(selectedOrgUnits);
      syncWithFilter &&
        setFilter(
          ORG_UNIT_FILTER_NAME,
          selectedOrgUnits.map(({ id }) =>
            orgUnitToFilter(orgUnitMap[id.toString()].orgUnit),
          ),
        );
    },
    [
      orgTreeState,
      orgUnitMap,
      setFilter,
      ctrlDown,
      disableOrgUnitSelectionBasedOnStatus,
      syncWithFilter,
      setSelected,
    ],
  );
};

export default useSelectOrgUnit;
